import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { confirmChannel } from '../../api/extension';
import { ConnectCodeCard } from '../../components/extension/ConnectCodeCard';
import { ExtensionHowToCard } from '../../components/extension/ExtensionHowToCard';
import { PersonalChannelsCard } from '../../components/extension/PersonalChannelsCard';
import { Skeleton } from '../../components/ui/skeleton';
import { useChannelList } from '../../hooks/use-channels';
import { useSession } from '../../hooks/use-session';
import { clearExtChallenge, isValidExtChallenge, readExtChallenge } from '../../lib/ext-challenge';

/**
 * "Расширение" (§3). Two jobs: hand the manager the one-time connect code
 * their side panel asks for, and let an owner/lead confirm the personal
 * channels that the extension registered.
 */
export function ExtensionPage() {
  const [params] = useSearchParams();
  // The challenge normally arrives in the URL. When this page is reached another
  // way — from the side menu, or after a login redirect that dropped the query —
  // the copy remembered for this tab (10 min TTL) keeps the button usable.
  const fromUrl = params.get('ext_challenge');
  const [remembered] = useState(() => readExtChallenge());
  const challenge = isValidExtChallenge(fromUrl) ? fromUrl : remembered;
  // Once a code is issued the pending request is spent: forget the stored copy.
  // The value stays in memory, so re-issuing an expired code on this page still works.
  const handleIssued = useCallback(() => clearExtChallenge(), []);
  const { me } = useSession();
  const { data, isLoading, isError } = useChannelList();
  const queryClient = useQueryClient();

  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canConfirm = me?.role === 'owner' || me?.role === 'lead';
  const channels = data ?? [];
  const personal = channels.filter((c) => c.type === 'vk_personal');
  const groups = channels.filter((c) => c.type === 'vk_group');

  const handleConfirm = useCallback(
    (id: string, linkedGroupChannelId: string | null) => {
      setBusyId(id);
      setError(null);
      void (async () => {
        try {
          await confirmChannel(id, { linked_group_channel_id: linkedGroupChannelId });
          await queryClient.invalidateQueries({ queryKey: ['channels'] });
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
        } finally {
          setBusyId(null);
        }
      })();
    },
    [queryClient],
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="border-b border-border px-4 py-3">
        <h1 className="text-lg font-semibold">Расширение</h1>
      </div>
      <div className="max-w-3xl space-y-4 p-4">
        <ConnectCodeCard challenge={challenge} onIssued={handleIssued} />
        {isLoading && <Skeleton className="h-24 w-full" />}
        {isError && <div className="text-sm text-red-600">Не удалось загрузить каналы.</div>}
        {!isLoading && !isError && (
          <PersonalChannelsCard
            personal={personal}
            groups={groups}
            canConfirm={canConfirm}
            busyId={busyId}
            error={error}
            onConfirm={handleConfirm}
          />
        )}
        <ExtensionHowToCard />
      </div>
    </div>
  );
}
